import type { JobListParams, SeparationProfileListParams } from './productionApi';

/**
 * Canonical React Query key factory for the /production/* subtree.
 *
 * All downstream plans (09-03..09-10) must import from here so invalidations
 * stay consistent. Never inline a literal ['production', ...] array elsewhere.
 */
export const productionKeys = {
  all: ['production'] as const,
  jobs: (params?: JobListParams) => ['production', 'jobs', params ?? {}] as const,
  schedule: (press: string, from?: string, to?: string) =>
    ['production', 'schedule', press, from ?? null, to ?? null] as const,
  // All presses in one response (the board). Lives under the same
  // ['production', 'schedule'] prefix so existing prefix invalidations
  // (JobDetailModal, SpeedReviewDialog) keep reaching it.
  scheduleAll: () => ['production', 'schedule', 'all'] as const,
  standards: (press: string) => ['production', 'standards', press] as const,
  customerColours: () => ['production', 'customer-colours'] as const,
  variance: (press: string) => ['production', 'variance', press] as const,
  syncStatus: () => ['production', 'sync-status'] as const,
  pressConfig: () => ['production', 'press-config'] as const,
  downtimeWindows: (press?: string) =>
    ['production', 'downtime-windows', press ?? null] as const,
  separationProfiles: (params?: SeparationProfileListParams) =>
    ['production', 'separation-profiles', params ?? {}] as const,
  separationProfile: (id: number) =>
    ['production', 'separation-profiles', id] as const,
  separationsResolveBulk: (press: string, keys: string[]) =>
    ['production', 'separation-profiles', 'resolve-bulk', press, [...keys].sort().join(',')] as const,
  jobSeparations: (jobNumber: string, press: string) =>
    ['production', 'job-separations', jobNumber, press] as const,
  artworkThumbnail: (jobNumber: string, width: number) =>
    ['production', 'artwork-thumbnail', jobNumber, width] as const,
  // Keyed on the Cloudflow path itself — the preview for artwork that has no
  // item code to look it up by (the ECG-vs-PMS screen's pasted paths).
  artworkThumbnailByPath: (path: string, width: number) =>
    ['production', 'artwork-thumbnail', 'path', path, width] as const,
  // One level of the Cloudflow file store, for the artwork path picker.
  artworkBrowse: (folder: string) => ['production', 'artwork-browse', folder] as const,
  // The nightly warm's failure worklist (migration 138). Invalidated by any
  // artwork re-pull: fixing a path clears that item's fail_count server-side,
  // so the list it came from is stale the moment the dialog saves.
  artworkWarmFailures: (params?: { kind?: string; search?: string; openOnly?: boolean }) =>
    ['production', 'artwork-warm-failures', params ?? {}] as const,
  artworkPathOverrides: () => ['production', 'artwork-path-overrides'] as const,
  // Prism print_spec vs artwork separations, every open job. Same invalidation
  // rule as the warm failures: any artwork re-pull changes the verdict.
  artworkSpecCheck: (params?: { scope?: string; status?: string; kind?: string; search?: string }) =>
    ['production', 'artwork-spec-check', params ?? {}] as const,
  // Where else a SKU's artwork might be filed (server-side string work, no
  // Cloudflow call) — stable for a given item, so the dialog can cache it hard.
  artworkPathSuggestions: (customer: string, ref: string) =>
    ['production', 'artwork-path-suggestions', customer, ref] as const,
  // The roll-up varies by mileage now that per-separation overrides mean it's no
  // longer a pure client-side scalar — so the query key carries it. Callers that
  // INVALIDATE pass no arg (the base prefix), which still matches every
  // mileage-keyed entry (React Query invalidation is prefix-based).
  inkDemandSummary: (mileage?: number) =>
    (mileage == null
      ? (['production', 'ink-demand-summary'] as const)
      : (['production', 'ink-demand-summary', mileage] as const)),
  // ECG-vs-PMS costing. The whole request (sheet size, run length, both paths,
  // every price) IS the key — nothing about it is stored server-side, so a
  // serialised request is the only stable identity a result has.
  inkComparison: (request: string) => ['production', 'ink-comparison', request] as const,
  inkAssignmentClients: () => ['production', 'ink-clients'] as const,
  inkAssignment: (client: string, showInactive = false) =>
    ['production', 'ink-assignment', client, showInactive] as const,
  inkSticker: (jobNumber: string) => ['production', 'ink-sticker', jobNumber] as const,
  inkSeries: (all?: boolean) => ['production', 'ink-series', !!all] as const,
  inkProducts: (params?: { includeInactive?: boolean; series?: string; search?: string; materialClass?: string }) =>
    ['production', 'ink-products', params ?? {}] as const,
  inkCoatings: (includeInactive?: boolean) => ['production', 'ink-coatings', !!includeInactive] as const,
  inkColourSwatches: (q?: string) => ['production', 'ink-colour-swatches', q ?? ''] as const,
  artworkColours: (q?: string) => ['production', 'artwork-colours', q ?? ''] as const,
  // Ink stock (migrations 109/110). `inkStock` is the roll-up screen; anything
  // that writes to the ledger must invalidate it AND inkBatches/inkTrolleyRuns,
  // since a single issue or reconcile moves all three.
  inkSuppliers: (all?: boolean) => ['production', 'ink-suppliers', !!all] as const,
  inkLocations: () => ['production', 'ink-locations'] as const,
  inkStock: (params?: { materialClass?: string; search?: string; belowReorder?: boolean; includeInactive?: boolean }) =>
    ['production', 'ink-stock', params ?? {}] as const,
  inkStockDetail: (inkKey: string) => ['production', 'ink-stock', 'detail', inkKey] as const,
  inkBatches: (params?: { inkKey?: string; supplierCode?: string; openOnly?: boolean }) =>
    ['production', 'ink-batches', params ?? {}] as const,
  inkLedger: (params?: { inkKey?: string; txnType?: string; trolleyRunId?: number; limit?: number }) =>
    ['production', 'ink-ledger', params ?? {}] as const,
  inkTrolleyRuns: (status?: string) => ['production', 'ink-trolley-runs', status ?? null] as const,
  inkTrolleyRun: (id: number) => ['production', 'ink-trolley-runs', id] as const,

  // Purchase orders (migration 113). Writing a PO or receiving against one must
  // also invalidate inkOnOrder (the balance report) and inkStock/inkBatches.
  inkPurchaseOrders: (params?: { supplier?: string; status?: string }) =>
    ['production', 'ink-purchase-orders', params ?? {}] as const,
  inkPurchaseOrder: (id: number) => ['production', 'ink-purchase-orders', id] as const,
  inkOnOrder: () => ['production', 'ink-on-order'] as const,
  // Ink demand forecast (migration 134). Keyed on the projection: a projection is
  // a snapshot, so two of them are genuinely different data, not a stale cache of
  // each other. `undefined` = whatever was imported most recently.
  inkForecastRuns: () => ['production', 'ink-forecast', 'runs'] as const,
  inkForecast: (projections?: string, customers?: string) =>
    ['production', 'ink-forecast', projections ?? null, customers ?? null] as const,

  // The board's forme groups, resolved server-side — a gang run is one of these.
  jobGroups: (params?: { press?: string; scheduled?: boolean; limit?: number }) =>
    ['production', 'job-groups', params ?? {}] as const,
  // Suggested trolley kit: ink demand narrowed to one group's jobs.
  inkDemandForJobs: (jobNumbers: string[], mileage?: number) =>
    ['production', 'ink-demand-for-jobs', [...jobNumbers].sort().join(','), mileage ?? null] as const,
  // Readiness varies by press AND by the lead times in play, so both are in the key.
  runReadiness: (press?: string, leads?: Record<string, number>) =>
    ['production', 'run-readiness', press ?? null, leads ?? null] as const,
};
